import AddIcon from '@mui/icons-material/Add';
import GetAppIcon from '@mui/icons-material/GetApp';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import SchoolIcon from '@mui/icons-material/School';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import {
  AppBar,
  Box,
  Checkbox,
  IconButton,
  LinearProgress,
  Paper,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import useToggle from '../../../../hooks/useToggle';
import { UserAttributesContext } from '../../../../shared-components/contexts/UserAttributesContext';
import UserContext from '../../../../shared-components/contexts/UserContext';
import NoItemBox from '../../../../shared-components/NoItemBox';
import { ICoreUser } from '../../../../shared-interfaces/User/ICoreUser';
import UserService, {
  SearchTypes,
} from '../../../../shared-services/User/UserService';
import ExportDownloadContainer from '../../UserManagement/Components/ExportDownloadContainer';
import { UserManagementActionType } from '../Types/UserManagementActionType';
import { UserManagementDispatch, UserManagementState } from '../UserManagement';
import ActionStepperDialog from './ActionStepperDialog';
import ChangeRetentionDialog from './ChangeRetentionStatusDialog';
import ChangeRoleDialog from './ChangeRoleDialog';
import UserRow, { UserRowLoading } from './UserRow';

export interface IUserManagementContentPanel {}

const useStyles = makeStyles(theme => ({
  heading: {
    backgroundColor: '#f9f9f9',
    padding: '10px',
    fontSize: '1.25rem',
    fontWeight: 500,
    margin: '0',
  },
  header: {
    top: '85px', // 85
    background: '#fff',
    zIndex: 998,
    borderBottom: '1px solid #dcdcdc',
    borderTop: '1px solid #dcdcdc',
    [theme.breakpoints.down('md')]: {
      top: '120px',
    },
  },
  chip: {
    marginTop: '5px',
    padding: '1px',
  },
  countText: { color: '#6f6f6f', padding: '8px' },
}));

const UserManagementContentPanel = () => {
  const classes = useStyles();

  const dispatch = useContext(UserManagementDispatch);
  const userAttributeContext = useContext(UserAttributesContext);

  const state = useContext(UserManagementState);
  const user = useContext(UserContext);

  const [openExportContainer, toggleExportContainer] = useToggle();
  const [showMultiUserSelectdialog, toggleShowMultiUserSelectDialog] =
    useToggle();
  const [showChangeRoleDialog, toggleShowChangeRoleDialog] = useToggle();
  const [showChangeRetentionDialog, toggleShowChangeRetentionDialog] =
    useToggle();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isInitalPageLoad, setIsInitialPageLoad] = useState<boolean>(true);

  const [totalUsers, setTotalUsers] = useState(0);

  const load = useCallback(() => {
    if (!isInitalPageLoad && state.users.length === totalUsers)
      return Promise.resolve();

    setIsLoading(true);

    return (async () => {
      const users = await UserService.search({
        term: state.filter.search,
        RoleIds: state.filter.roles,
        RetentionStatusIds: state.filter.retentionStatus,
        take: 50,
        skip: state.users.length,
        inNetwork: false,
        enrolledOnly: false,
        SearchType: SearchTypes.like,
      });
      dispatch({
        type: UserManagementActionType.SetUsers,
        users: [...state.users, ...users.items],
      });
      setTotalUsers(users.totalCount);
      setIsLoading(false);
    })();
  }, [state.users]);

  useEffect(() => {
    load().finally(() => {
      setIsInitialPageLoad(false);
    });
  }, []);

  useEffect(() => {
    if (!isInitalPageLoad) {
      dispatch({
        type: UserManagementActionType.SetUsers,
        users: [] as ICoreUser[],
      });
      if (state.selectAll) {
        dispatch({
          type: UserManagementActionType.SelectAll,
        });
      }
      setIsLoading(true);
      loadUsers(0);
    }
  }, [state.filter]);

  const loadUsers = useCallback(
    (skip: number) => {
      setIsLoading(true);
      UserService.search({
        term: state.filter.search,
        RoleIds: state.filter.roles,
        RetentionStatusIds: state.filter.retentionStatus,
        take: 50,
        skip: skip,
        inNetwork: false,
        enrolledOnly: false,
        SearchType: SearchTypes.like,
      })
        .then(u => {
          dispatch({
            type: UserManagementActionType.SetUsers,
            users: u.items,
          });
          setTotalUsers(u.totalCount);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [state.filter],
  );

  useEffect(() => {
    dispatch({ type: UserManagementActionType.ExcludeUsers, value: [] });
    dispatch({ type: UserManagementActionType.SetSelectedUsers, value: [] });
  }, [state.selectAll]);

  function getUserRetentionStatus(userId: string) {
    if (userId in state.userRetentionStatuses) {
      return userAttributeContext.retentionStatuses.find(
        x => x.id === state.userRetentionStatuses[userId].statusId,
      );
    } else {
      return undefined;
    }
  }

  function OnUserSelect(user: ICoreUser) {
    if (state.selectAll) {
      if (state.excludeUsers.findIndex(y => y.id === user.id) === -1) {
        dispatch({
          type: UserManagementActionType.ExcludeUsers,
          value: [...state.excludeUsers, user],
        });
      } else {
        dispatch({
          type: UserManagementActionType.ExcludeUsers,
          value: state.excludeUsers.filter(y => y.id !== user.id),
        });
      }
    } else {
      dispatch({
        type:
          state.selectedUsers.findIndex(y => y.id === user.id) === -1
            ? UserManagementActionType.SelectUsers
            : UserManagementActionType.RemoveUsers,
        value: [user],
      });
    }
  }

  return (
    <>
      <Paper elevation={2} square style={{ paddingBottom: '1px', margin: 0 }}>
        <Typography variant="h2" className={classes.heading} component="h2">
          Users
        </Typography>
        <AppBar position="sticky" className={classes.header} elevation={0}>
          <Toolbar variant="dense" style={{ padding: '0 0 0 30px' }}>
            <Box display="flex" style={{ width: '100%' }}>
              <Box flexGrow={1}>
                <Tooltip title="Select All">
                  <Checkbox
                    color="primary"
                    inputProps={{ 'aria-label': 'select all' }}
                    edge="start"
                    disabled={isLoading}
                    indeterminate={
                      state.selectedUsers.length !== 0 ||
                      state.excludeUsers.length !== 0
                    }
                    checked={
                      state.selectAll || state.selectedUsers.length !== 0
                    }
                    onClick={() => {
                      dispatch({ type: UserManagementActionType.SelectAll });
                    }}
                  />
                </Tooltip>
                <Tooltip title="Select Multiple Users">
                  <IconButton
                    onClick={() => toggleShowMultiUserSelectDialog()}
                    aria-label="Select Multiple Users"
                    size="small"
                  >
                    <AddIcon />
                  </IconButton>
                </Tooltip>

                {user.isAdministrator && (
                  <>
                    <Tooltip title="Change Role">
                      <IconButton
                        onClick={() => toggleShowChangeRoleDialog()}
                        aria-label="Change Role"
                        size="small"
                      >
                        <SupervisorAccountIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Change Retention Status">
                      <IconButton
                        onClick={() => toggleShowChangeRetentionDialog()}
                        aria-label="Change Retention Status"
                        size="small"
                      >
                        <SchoolIcon />
                      </IconButton>
                    </Tooltip>
                  </>
                )}

                <Tooltip
                  title={`${
                    openExportContainer ? 'Hide' : 'Show'
                  } Connections Export`}
                >
                  <IconButton onClick={() => toggleExportContainer()}>
                    <GetAppIcon />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box>
                {!isInitalPageLoad && (
                  <Typography
                    aria-live="assertive"
                    className={classes.countText}
                  >{`Showing ${state.users.length.toLocaleString()} of ${totalUsers.toLocaleString()}`}</Typography>
                )}
              </Box>
            </Box>
          </Toolbar>
        </AppBar>
        <>
          <ExportDownloadContainer
            showExportDownloadContainer={openExportContainer}
          />
        </>
        <div>
          {state.users.length === 0 &&
            isLoading &&
            [...Array(50)].map((_e, i) => <UserRowLoading key={i} />)}
          {state.users.length === 0 && !isLoading && (
            <NoItemBox
              height={575}
              minHeight={497}
              text="No Users to Display"
            />
          )}
          {state.users.length > 0 && (
            <InfiniteScroll
              element="ul"
              loadMore={() => load()}
              threshold={500}
              initialLoad={false}
              hasMore={totalUsers > state.users.length && !isLoading}
              useWindow={true}
              style={{
                padding: '0 0 0 3px',
              }}
            >
              {state.users.map(u => (
                <UserRow
                  key={u.id}
                  user={u}
                  retentionStatus={getUserRetentionStatus(u.id)}
                  selected={
                    state.selectedUsers.findIndex(y => y.id === u.id) !== -1 ||
                    (state.selectAll &&
                      state.excludeUsers.findIndex(y => y.id === u.id) === -1)
                  }
                  onClick={() => OnUserSelect(u)}
                />
              ))}
            </InfiniteScroll>
          )}
        </div>
        {isLoading &&
          state.users.length > 0 &&
          totalUsers > state.users.length && (
            <div className="loading-bar-container">
              <LinearProgress />
            </div>
          )}
      </Paper>

      <ActionStepperDialog
        open={showMultiUserSelectdialog}
        close={toggleShowMultiUserSelectDialog}
      />
      <ChangeRoleDialog
        open={showChangeRoleDialog}
        close={toggleShowChangeRoleDialog}
        entryMethod="BulkOps"
      />
      <ChangeRetentionDialog
        open={showChangeRetentionDialog}
        close={toggleShowChangeRetentionDialog}
        entryMethod="BulkOps"
      />
    </>
  );
};

export default UserManagementContentPanel;
